import { BaseUITools } from '@/uiControls/base/baseUITools'
import i18NextClass from '../i18n/index.ts';
import { globalConfig, globalManager, modelDialogTools, globalTools } from '@/utils/globalConfig.ts';
import { sceneUtils } from '@/utils/sceneUtils.ts';

import { CheckButtonGroupUI } from './base/ui/checkButtonGroupUI.ts';
import { getI18nText, openHomepage } from '@/utils/commonFunc.ts';
import i18next from 'i18next';

export class ToolbarNavTools extends BaseUITools {
    private rightdomid: string
    private checkButtonGroup: CheckButtonGroupUI | null = null;

    constructor(id: string, rightid: string) {
        super(id);
        this.rightdomid = rightid
        this.initTools();
    }

    initTools() {
        this.createToolbarScene();
        this.createToolbarMode();
        this.createToolbarRender();
        this.createToolbarHelp();
        this.createToolbarRightlink();
        this.initHomepageLink();
        this.initEventHandlers();
    }

    private initHomepageLink(): void {
        const homepageLink = document.querySelector('.homepage-link');
        if (homepageLink) {
            homepageLink.addEventListener('click', (e) => {
                e.preventDefault();
                openHomepage();
            });
        }
    }

    private initEventHandlers(): void {
        window.addEventListener('event.plyHeatmapLoaded', () => {
            if (!globalConfig.currentRenderMode || globalConfig.currentRenderMode !== globalConfig.renderMode.heatmap) {
                this.clickToolbarModeHeatmapItem();
            }
        });
    }

    /** 与用户在「模式」下拉里点击 Heatmap 一致（含 remberCurrent 与下拉关闭逻辑） */
    private clickToolbarModeHeatmapItem(): void {
        const modeContainer = this.getChild('toolbar-nav-mode');
        const heatmapItem = modeContainer?.querySelector<HTMLElement>('.dropdown-item.mode-heatmap');
        if (heatmapItem) {
            heatmapItem.click();
        } else {
            this.handleSetSceneRenderMode(globalConfig.renderMode.heatmap);
        }
    }
    /**
     * 设置场景视窗，单视窗时，保存按钮可用，多视窗时，保存按钮不可用
     * @param isSingle 是否单视窗
     */
    public setSceneWindowSingle(isSingle: boolean): void {
        if (isSingle) {
            this.checkButtonGroup?.setActiveButton('single-window');
            const mainAppContainerDom = document.getElementById(globalConfig.domIds.mainAppContainer);
            if (mainAppContainerDom) { // 单视窗时，保存场景按钮可用
                mainAppContainerDom.classList.remove('multi-window-app-container');
            }
        } else {
            this.checkButtonGroup?.setActiveButton('multi-window');
            // 多视窗时，保存场景按钮不可用，禁用功能在sceneUtils.setMultiWindow()中实现
        }
    }

    // 场景
    private createToolbarScene(): void {
        const sceneNavtool = this.ui.createDropdownTab({
            id: 'toolbar-nav-scene',
            title: '场景',
            hasChild: true,
            className: 'toolbar-nav-scene-dropdown',
            i18nKey: 'nav.scene'
        });

        // 添加场景相关的子tab
        sceneNavtool.addItem({ title: '新建场景', i18nKey: 'nav.newScene' }, () => {
            globalTools?.timelinePanelTools?.handleAnimationPlay(false); // 停止动画播放
            this.handleNewScene();
        }, {
            icon: this.ui.createIcon({
                iconName: 'FileOutlined1',
                size: 'sm',
                className: 'scene-new'
            }),
            className: 'scene-new'
        });

        sceneNavtool.addItem({ title: '打开场景', i18nKey: 'nav.openScene' }, () => {
            sceneUtils.handleOpenScene();
        }, {
            icon: this.ui.createIcon({
                iconName: 'FolderOutlined1',
                size: 'sm',
                className: 'scene-open'
            }),
            className: 'scene-open'
        });

        sceneNavtool.addItem({ title: '保存场景', i18nKey: 'nav.saveScene' }, () => {
            globalTools?.timelinePanelTools?.handleAnimationPlay(false); // 停止动画播放
            this.handleSaveScene();
        }, {
            id: globalConfig.domIds.saveSceneButton,
            icon: this.ui.createIcon({
                iconName: 'SavefileOutlined',
                size: 'sm',
                className: 'scene-save'
            }),
            className: ['scene-save']
        });

        this.appendChild('toolbar-nav-scene', sceneNavtool.getContainer());
    }
    // 多视窗、示例、教程、语言、github等导航按钮
    private createToolbarRightlink(): void {
        let parentDom = document.getElementById(this.rightdomid)
        if (!parentDom) {
            console.log("父dom没有定义")
            return;
        }
        const checkButtonGroup = this.ui.createCheckButtonGroup({
            items: [
                {
                    id: 'single-window',
                    title: '单视窗',
                    value: 'single-window',
                    i18nKey: 'nav.singleWindow',
                    iconName: 'SingleViewportOutline',
                    titleI18nKey: 'nav.singleWindowTitle',
                    onClickEvent: () => {
                        if (window.gtagTracking && typeof window.gtagTracking === 'function') {
                            window.gtagTracking('click', 'singleWindow', 'single-window');
                        }
                        globalTools?.timelinePanelTools?.handleAnimationPlay(false); // 停止动画播放
                        return sceneUtils.setSingleWindow();
                    }
                },
                {
                    id: 'multi-window',
                    title: '多视窗',
                    value: 'multi-window',
                    i18nKey: 'nav.multiWindow',
                    iconName: 'DualViewportOutline',
                    titleI18nKey: 'nav.multiWindowTitle',
                    onClickEvent: async () => {
                        if (window.gtagTracking && typeof window.gtagTracking === 'function') {
                            window.gtagTracking('click', 'multiWindow', 'multi-window');
                        }
                        globalTools?.timelinePanelTools?.handleAnimationPlay(false); // 停止动画播放
                        const result = await sceneUtils.setMultiWindow();
                        return result;
                    }
                }
            ],
            direction: 'horizontal',
            defaultActiveId: 'single-window',
            containerClass: ''
        });
        this.checkButtonGroup = checkButtonGroup;
        parentDom.appendChild(checkButtonGroup.getContainer());

        // 添加分隔线
        const separator = this.ui.createElement({
            tag: 'div',
            className: 'toolbar-separator',
            styles: {
                alignSelf: 'center'
            }
        });
        parentDom.appendChild(separator);

        const exampleNavtool = this.ui.createDropdownTab({
            id: 'toolbar-nav-example',
            title: '示例',
            i18nKey: 'nav.example',
            hasChild: false
        });
        exampleNavtool.getContainer().addEventListener('click', () => {
            modelDialogTools.openDemoViewDialog();
        });
        parentDom.appendChild(exampleNavtool.getContainer());

        const teachNavtool = this.ui.createDropdownTab({
            id: 'toolbar-nav-teach',
            title: '教程',
            i18nKey: 'nav.teach',
            hasChild: false
        });
        teachNavtool.getContainer().addEventListener('click', () => {
            console.log(i18next.language);
            console.log(import.meta.env.VITE_TEACH_PAGE_URL);
            console.log(import.meta.env.VITE_TEACH_EN_PAGE_URL);

            let teachPageUrl = import.meta.env.VITE_TEACH_PAGE_URL || 'https://github.com/Visionary-Laboratory/visionary';
            if (i18next.language === 'en') {
                teachPageUrl = import.meta.env.VITE_TEACH_EN_PAGE_URL || 'https://github.com/Visionary-Laboratory/visionary';
            }
            window.open(teachPageUrl, '_blank');
        });
        parentDom.appendChild(teachNavtool.getContainer());


        const languageTools = this.ui.createElement({
            tag: 'div',
            id: "langController"
        });



        parentDom.appendChild(languageTools);

        const githubNavtool = this.ui.createDropdownTab({
            id: 'toolbar-nav-github',
            title: 'github',
            iconName: 'GithubFilled',
            hasChild: false
        });
        githubNavtool.getContainer().addEventListener('click', () => {
            const githubUrl = import.meta.env.VITE_GITHUB_PATH_URL || 'https://github.com/Visionary-Laboratory/visionary';
            window.open(githubUrl, '_blank');
        });
        parentDom.appendChild(githubNavtool.getContainer());
    }

    // 模式
    private createToolbarMode(): void {
        const modeNavtool = this.ui.createDropdownTab({
            id: 'toolbar-nav-mode',
            title: '模式',
            hasChild: true,
            remberCurrent: true,
            i18nKey: 'nav.renderModel'
        });

        // 添加模式相关的子tab
        modeNavtool.addItem({ title: '普通', i18nKey: 'nav.normal' }, () => {
            this.handleSetSceneRenderMode(globalConfig.renderMode.normal);
        }, {
            icon: this.ui.createIcon({
                iconName: 'ModelCardOutlined',
                size: 'sm',
                className: 'mode-normal'
            }),
            className: 'mode-normal',
            isDefault: true
        });

        // modeNavtool.addItem({ title: '点云', i18nKey: 'nav.pointcloud' }, () => { // 点云模式暂时不支持
        //     this.handleSetSceneRenderMode(globalConfig.renderMode.pointcloud);
        // }, {
        //     icon: this.ui.createIcon({
        //         iconName: 'PointCloudOutlined',
        //         size: 'sm',
        //         className: 'mode-pointcloud'
        //     }),
        //     className: 'mode-pointcloud'
        // });

        modeNavtool.addItem({ title: '深度', i18nKey: 'nav.depth' }, () => {
            this.handleSetSceneRenderMode(globalConfig.renderMode.depth);
        }, {
            icon: this.ui.createIcon({
                iconName: 'DepthOutlined',
                size: 'sm',
                className: 'mode-depth'
            }),
            className: 'mode-depth'
        });

        modeNavtool.addItem({ title: '法线', i18nKey: 'nav.normalmap' }, () => {
            this.handleSetSceneRenderMode(globalConfig.renderMode.normalmap);
        }, {
            icon: this.ui.createIcon({
                iconName: 'NormalOutlined',
                size: 'sm',
                className: 'mode-normalmap'
            }),
            className: 'mode-normalmap'
        });

        // modeNavtool.addItem({ title: 'PCA特征', i18nKey: 'nav.pcaFeature' }, () => {
        //     this.handleSetSceneRenderMode(globalConfig.renderMode.pcaFeature);
        // }, {
        //     icon: this.ui.createIcon({
        //         iconName: 'PointCloudOutlined',
        //         size: 'sm',
        //         className: 'mode-pca-feature'
        //     }),
        //     className: 'mode-pca-feature'
        // });

        modeNavtool.addItem({ title: 'Heatmap', i18nKey: 'nav.heatmap' }, () => {
            this.handleSetSceneRenderMode(globalConfig.renderMode.heatmap);
        }, {
            icon: this.ui.createIcon({
                iconName: 'HeatmapOutlined1',
                size: 'sm',
                className: 'mode-heatmap'
            }),
            className: 'mode-heatmap'
        });


        this.appendChild('toolbar-nav-mode', modeNavtool.getContainer());
    }

    private createToolbarRender(): void {
        const renderNavtool = this.ui.createDropdownTab({
            id: 'toolbar-nav-render',
            title: '渲染',
            hasChild: true,
            i18nKey: 'nav.render'
        });

        // 添加渲染相关的子tab
        renderNavtool.addItem({ title: '图像', i18nKey: 'nav.image' }, () => {
            this.handleImageRender();
        }, {
            icon: this.ui.createIcon({
                iconName: 'PictureOutlined',
                size: 'sm',
                className: 'render-image'
            }),
            className: 'render-image'
        });

        renderNavtool.addItem({ title: '视频', i18nKey: 'nav.video' }, () => {
            this.handleVideoRender();
        }, {
            icon: this.ui.createIcon({
                iconName: 'VideoOutlined',
                size: 'sm',
                className: 'render-video'
            }),
            className: 'render-video'
        });

        this.appendChild('toolbar-nav-render', renderNavtool.getContainer());
    }

    private createToolbarHelp(): void {
        const helpNavtool = this.ui.createDropdownTab({
            id: 'toolbar-nav-help',
            title: '关于',
            hasChild: false,
            i18nKey: 'nav.about'
        });
        helpNavtool.getContainer().addEventListener('click', () => {
            this.handleHelp();
        });
        this.appendChild('toolbar-nav-help', helpNavtool.getContainer());
    }

    /**
     * 处理新建场景
     */
    private handleNewScene(): void {
        const newSceneTitle = getI18nText('message.newScene.title');
        const newSceneConfirm = getI18nText('message.newScene.confirm');
        modelDialogTools.showConfirmDialog(
            newSceneTitle,
            newSceneConfirm,
            () => {
                // 这里可以添加具体的业务逻辑
                // 例如：清空当前场景、重置状态等
                modelDialogTools.openSceneDialog();
            },
            () => {
                console.log('用户取消新建场景');
            }
        );
    }

    /**
     * 处理保存场景
     */
    private handleSaveScene(): void {
        modelDialogTools.openSaveSceneDialog();
    }

    /**
     * 处理普通模式
     */
    private handleSetSceneRenderMode(mode: number): void {
        sceneUtils.setSceneRenderMode(mode);
    }

    /**
     * 处理图像渲染
     */
    private handleImageRender(): void {
        // 这里可以添加具体的业务逻辑
        // 例如：设置图像渲染模式、更新UI状态等
        modelDialogTools.openImageDialog();
    }

    /**
     * 处理视频渲染
     */
    private handleVideoRender(): void {
        const targetObject = globalManager.getCurrentObject();
        if (targetObject && targetObject.type === 'recordingCamera') {
            console.log('[VideoExport][UI] 用户点击「渲染 > 视频」，当前选中对象为录制相机:', {
                objectId: (targetObject as any).id,
                name: (targetObject as any).name,
                sceneId: (targetObject as any).sceneid,
                type: targetObject.type,
            });
            modelDialogTools.openVideoDialog();
        } else {
            // console.log('[VideoExport][UI] 用户点击「渲染 > 视频」，但当前未选中录制相机，弹出提示');
            modelDialogTools.showMessageModal({
                title: getI18nText('message.exportVideo.title'),
                message: getI18nText('message.exportVideo.information'),
                duration: 0,
            });
            return;
        }
    }

    // /**
    //  * 处理使用指南
    //  */
    private handleHelp(): void {
        modelDialogTools.setAboutMessageModal(true);
    }

}